import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { TiArrowLeftThick } from 'react-icons/ti'

import p5 from 'p5'

export const Route = createFileRoute('/frc-360-logo')({
  component: FrcLogoPage,
})

function FrcLogoPage() {
  const sketchRef = useRef<HTMLDivElement>(null)
  const [fgColor, setFgColor] = useState('#000000')
  const [bgColor, setBgColor] = useState('#ffffff')
  const p5Instance = useRef<p5 | null>(null)
  const p5Renderer = useRef<p5.Renderer | null>(null)
  const fgColorRef = useRef('#000000')
  const bgColorRef = useRef('#ffffff')

  // Update refs when colors change
  useEffect(() => {
    fgColorRef.current = fgColor
  }, [fgColor])

  useEffect(() => {
    bgColorRef.current = bgColor
  }, [bgColor])

  useEffect(() => {
    if (!sketchRef.current) return

    let imageSideLength = 600
    let penSize: number
    let diameter: number
    let radius: number
    let cogToothLength = 1.25
    let cogToothWidth = 1
    let ctr: { x: number; y: number }
    let imageFont: p5.Font | null = null

    // Local variable to capture the specific instance for this effect cycle
    let myP5: p5;

    const sketch = (p: p5) => {
      p.setup = () => {
        if(p5Renderer.current) {
          p5Renderer.current.remove()
          p5Instance.current?.remove()
        }

        p5Renderer.current = p.createCanvas(imageSideLength, imageSideLength)
        p.pixelDensity(2.0)
        
        // Load font with callback
        p.loadFont('font/Courgette-Regular.ttf', (font) => {
          imageFont = font
          console.log('Font loaded successfully')
          p.redraw()
        }, (err) => {
          console.error('Font loading error:', err)
        })
        
        p.noLoop()
      }

      p.draw = () => {
        if (!imageFont) {
          return
        }

        penSize = imageSideLength * 0.08
        diameter = imageSideLength * 0.75
        radius = diameter / 2
        ctr = {
          x: imageSideLength / 2,
          y: imageSideLength / 2
        }

        // Parse colors from hex strings - use refs to get current values
        const fg = p.color(fgColorRef.current)
        const bg = p.color(bgColorRef.current)

        p.background(bg)
        p.strokeWeight(penSize)
        p.stroke(fg)

        // the circle
        p.noFill()
        p.circle(ctr.x, ctr.y, diameter)

        // the cog teeth
        p.strokeCap(p.SQUARE)
        p.strokeWeight(penSize * cogToothWidth)
        for (let a = p.PI / 2; a <= 3 * p.PI / 2; a += p.PI / 8) {
          let startX = ctr.x + radius * p.cos(a)
          let startY = ctr.y + radius * p.sin(a)
          let endX = ctr.x + radius * p.cos(a) * cogToothLength
          let endY = ctr.y + radius * p.sin(a) * cogToothLength
          p.line(startX, startY, endX, endY)
        }

        // the vertical line for the 'R'
        let startX = ctr.x + radius * p.cos(p.PI / 2)
        let startY = ctr.y + radius * p.sin(p.PI / 2)
        let endX = ctr.x + radius * p.cos(3 * p.PI / 2)
        let endY = ctr.y + radius * p.sin(3 * p.PI / 2)
        p.line(startX, startY, endX, endY)

        // the top line of the R
        p.noFill()
        p.bezier(
          ctr.x,
          ctr.y,
          ctr.x + radius,
          ctr.y,
          ctr.x + 7 / 8 * radius,
          ctr.y - radius,
          ctr.x,
          ctr.y - radius
        )

        // the bottom leg of the R
        p.line(
          ctr.x + penSize * 0.3,
          ctr.y + penSize * 0.2,
          ctr.x + radius * p.cos(p.PI / 4),
          ctr.y + radius * p.sin(p.PI / 4)
        )

        // draw one cog tooth for the R leg
        p.line(
          ctr.x + radius * p.cos(p.PI / 4),
          ctr.y + radius * p.sin(p.PI / 4),
          ctr.x + radius * p.cos(p.PI / 4) * cogToothLength,
          ctr.y + radius * p.sin(p.PI / 4) * cogToothLength
        )

        // draw a triangle to cap that cog tooth
        let endcapX = ctr.x + radius * p.cos(p.PI / 4)
        let endcapY = ctr.y + radius * p.sin(p.PI / 4)
        p.fill(fg)
        p.noStroke()

        p.triangle(
          endcapX + 5 * penSize / 3,
          endcapY + 5 * penSize / 3,
          endcapX,
          endcapY + 3 * penSize / 2,
          endcapX + 3 * penSize / 2,
          endcapY
        )

        // the spiral
        p.stroke(fg)
        p.strokeWeight(penSize / 3)
        let xOffset = 5 / 4 * penSize
        let yOffset = radius - penSize
        let yWalkParam = 0.013 * imageSideLength
        let yAmp = penSize / 2
        for (let t = p.PI / 4; t < 111 * p.PI / 8; t += p.PI / 128) {
          p.point(
            ctr.x - xOffset + 3 / 4 * penSize * p.cos(t),
            ctr.y - yOffset - yAmp * p.sin(t) + yWalkParam * t
          )
        }

        // the inner circle (hub spoke)
        p.noStroke()
        p.fill(bg)
        p.circle(ctr.x, ctr.y, penSize)

        // top-right text "360"
        p.fill(fg)
        let textAngle = 5 * p.PI / 32
        p.push()
        p.translate(
          (ctr.x + (radius + penSize / 2) * p.sin(textAngle)) * 0.995,
          (ctr.y - (radius + penSize / 2) * p.cos(textAngle)) * 0.995
        )
        p.rotate(textAngle * 1.25)
        p.textFont(imageFont)
        p.textSize(penSize)
        p.text('360', 0, 0)
        p.pop()
      }
    }

    myP5 = new p5(sketch, sketchRef.current)
    p5Instance.current = myP5

    return () => myP5?.remove()
  }, [])

  // Redraw when colors change
  useEffect(() => {
    if (p5Instance.current) {
      p5Instance.current.redraw()
    }
  }, [fgColor, bgColor])

  const handleSave = () => {
    if (p5Instance.current) {
      const now = new Date()
      const fileName = `frc360-oldlogo-${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}-${now.getHours()}-${now.getMinutes()}-${now.getSeconds()}.png`
      p5Instance.current.save(fileName)
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white rounded-lg shadow-md p-8 mb-8">
        <div className="mb-6">
          <a href="/" className="text-blue-600 hover:text-blue-800 flex items-center gap-2"><TiArrowLeftThick />
 Home</a>
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          FRC Team 360 (<a href="http://frc360.com/" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">frc360.com</a>) Logo from the early 2000's
        </h2>
        
        <div className="prose max-w-none mb-6 text-gray-700">
          <p className="mb-4">
            This image was made and is displayed below using <a href="http://p5js.org" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">p5js</a>. 
            The "360" in the top right corner is in the <a href="https://fonts.google.com/specimen/Courgette" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Courgette font</a> which 
            is licensed under the <a href="font/OFL.txt" className="text-blue-600 hover:underline">OFL</a>. 
            Please note, this is a recreation of the original; it cannot be and is not intended to be pixel-perfect, 
            and the font used here is not the one originally used (but is pretty close, stylistically).
          </p>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-gray-700 mb-2">Pick a color for the background (default white):</label>
            <input
              type="color"
              value={bgColor}
              onChange={(e) => setBgColor(e.target.value)}
              className="w-20 h-10 border border-gray-300 rounded cursor-pointer"
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-2">Pick a color for the foreground (default black):</label>
            <input
              type="color"
              value={fgColor}
              onChange={(e) => setFgColor(e.target.value)}
              className="w-20 h-10 border border-gray-300 rounded cursor-pointer"
            />
          </div>

          <div>
            <p className="text-gray-700 mb-2">Click this button to save a PNG of the image as you have it configured (size & colors):</p>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium"
            >
              Save Image File
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-8 flex justify-center">
        <div ref={sketchRef} />
      </div>
    </div>
  )
}